import { CapacityMember, FailedOp, ProductOp } from 'capacity/api/capacityGateway'

export interface StagedAdd {
    member: CapacityMember
    amountUsed: number
}

export interface CapacityDraft {
    adds: Record<string, StagedAdd>
    removals: Record<string, true>
    amountChanges: Record<string, number>
    failed: Record<string, string>
}

export const emptyDraft: CapacityDraft = { adds: {}, removals: {}, amountChanges: {}, failed: {} }

export const USES_MIN = 1
export const USES_MAX = 10000

export type DraftAction =
    | { type: 'stageAdd'; member: CapacityMember; amountUsed?: number }
    | { type: 'unstageAdd'; productId: string }
    | { type: 'toggleRemoval'; productId: string }
    | { type: 'setAmount'; productId: string; amount: number; baseline: number }
    | { type: 'setAddAmount'; productId: string; amount: number }
    | { type: 'markFailed'; failed: FailedOp[] }
    | { type: 'commitSucceeded'; ops: ProductOp[] }
    | { type: 'reset' }

const clampUses = (amount: number): number => Math.max(USES_MIN, Math.min(USES_MAX, Math.round(amount)))

const without = <T>(record: Record<string, T>, key: string): Record<string, T> => {
    const { [key]: _removed, ...rest } = record
    return rest
}

export function draftReducer(state: CapacityDraft, action: DraftAction): CapacityDraft {
    switch (action.type) {
        case 'stageAdd':
            return {
                ...state,
                adds: {
                    ...state.adds,
                    [action.member.id]: { member: action.member, amountUsed: clampUses(action.amountUsed ?? 1) },
                },
                failed: without(state.failed, action.member.id),
            }
        case 'unstageAdd':
            return {
                ...state,
                adds: without(state.adds, action.productId),
                failed: without(state.failed, action.productId),
            }
        case 'toggleRemoval':
            if (state.adds[action.productId]) {
                return draftReducer(state, { type: 'unstageAdd', productId: action.productId })
            }
            return {
                ...state,
                removals: state.removals[action.productId]
                    ? without(state.removals, action.productId)
                    : { ...state.removals, [action.productId]: true },
                amountChanges: without(state.amountChanges, action.productId),
                failed: without(state.failed, action.productId),
            }
        case 'setAmount': {
            const amount = clampUses(action.amount)
            return {
                ...state,
                amountChanges:
                    amount === action.baseline
                        ? without(state.amountChanges, action.productId)
                        : { ...state.amountChanges, [action.productId]: amount },
                failed: without(state.failed, action.productId),
            }
        }
        case 'setAddAmount': {
            const add = state.adds[action.productId]
            if (!add) {
                return state
            }
            return {
                ...state,
                adds: { ...state.adds, [action.productId]: { ...add, amountUsed: clampUses(action.amount) } },
            }
        }
        case 'markFailed':
            return {
                ...state,
                failed: action.failed.reduce<Record<string, string>>(
                    (all, entry) => ({ ...all, [entry.op.productId]: entry.message }),
                    {},
                ),
            }
        case 'commitSucceeded': {
            let next = state
            for (const op of action.ops) {
                next = {
                    adds: without(next.adds, op.productId),
                    removals: without(next.removals, op.productId),
                    amountChanges: without(next.amountChanges, op.productId),
                    failed: without(next.failed, op.productId),
                }
            }
            return next
        }
        case 'reset':
            return emptyDraft
        default:
            return state
    }
}

export type RowStatus = 'new' | 'removing' | 'changed' | 'kept'

export const rowStatus = (draft: CapacityDraft, productId: string): RowStatus => {
    if (draft.adds[productId]) {
        return 'new'
    }
    if (draft.removals[productId]) {
        return 'removing'
    }
    if (draft.amountChanges[productId] !== undefined) {
        return 'changed'
    }
    return 'kept'
}

export const hasDraftChanges = (draft: CapacityDraft): boolean =>
    Object.keys(draft.adds).length + Object.keys(draft.removals).length + Object.keys(draft.amountChanges).length > 0

export const accountWideAmount = (member: CapacityMember): number | null =>
    member.links.find((link) => link.productListId === null)?.amountUsed ?? null

export const effectiveAmount = (draft: CapacityDraft, member: CapacityMember): number | null => {
    const staged = draft.adds[member.id]
    if (staged) {
        return staged.amountUsed
    }
    const changed = draft.amountChanges[member.id]
    if (changed !== undefined) {
        return changed
    }
    return accountWideAmount(member)
}

const memberFromOp = (op: ProductOp): CapacityMember => ({
    kind: 'product',
    id: op.productId,
    name: op.label,
    businessType: null,
    isBundle: false,
    categoryIds: [],
    inactiveCategoryIds: [],
    links: [],
})

export function draftAfterFailedCreate(draft: CapacityDraft, failed: FailedOp[]): CapacityDraft | undefined {
    if (failed.length === 0) {
        return undefined
    }
    const staged = failed
        .filter(({ op }) => op.kind === 'add')
        .reduce<CapacityDraft>((next, { op }) => {
            const member = draft.adds[op.productId]?.member ?? memberFromOp(op)
            return draftReducer(next, { type: 'stageAdd', member, amountUsed: op.amountUsed })
        }, emptyDraft)
    return draftReducer(staged, { type: 'markFailed', failed })
}
